/**
 * Cart ARIA Live Region Announcements
 *
 * This module provides screen reader announcements for cart contents and updates.
 *
 * CART PAGE (page reload mode):
 * - Announces full cart contents on page load after any quantity change
 * - Example: "Cart has 2 items: Product A, quantity 3; Product B, quantity 1. Cart total: $45.00"
 *
 * MINI-CART (AJAX mode):
 * - Announces individual quantity changes in real-time
 * - Example: "Product A quantity increased to 3. Cart total: $45.00"
 *
 * @accessibility WCAG 2.1 4.1.3 Status Messages (Level AA)
 * @created 2025-11-14
 */

(function() {
  'use strict';

  /**
   * Initialize ARIA live regions and attach event listeners
   */
  function init() {
    // Only run on cart page or when mini-cart is present
    if (!isCartPage() && !document.querySelector('.mini-cart')) {
      return;
    }

    // Create live region containers on page load
    createLiveRegions();

    // On cart page, announce current cart contents on page load
    if (isCartPage()) {
      announceCartContentsOnLoad();
    }

    // Attach listeners for quantity changes
    attachQuantityListeners();

    // Listen for cart refresh events (used by the theme)
    document.addEventListener('cart:refresh', handleCartRefresh);
  }

  /**
   * Check if current page is the cart page
   */
  function isCartPage() {
    return window.theme && window.theme.pageType === 'cart';
  }

  /**
   * Create ARIA live region containers
   * These must exist on page load (empty) for screen readers to detect them
   */
  function createLiveRegions() {
    // Check if already created
    if (document.getElementById('cart-status-announcements')) {
      return;
    }

    // Create polite live region for non-urgent updates (quantity/price changes)
    var politeRegion = document.createElement('div');
    politeRegion.id = 'cart-status-announcements';
    politeRegion.setAttribute('role', 'status');
    politeRegion.setAttribute('aria-live', 'polite');
    politeRegion.setAttribute('aria-atomic', 'true');
    politeRegion.className = 'visually-hidden';
    politeRegion.textContent = ''; // Must be empty on load

    // Create assertive live region for urgent updates (errors)
    var assertiveRegion = document.createElement('div');
    assertiveRegion.id = 'cart-error-announcements';
    assertiveRegion.setAttribute('role', 'alert');
    assertiveRegion.setAttribute('aria-live', 'assertive');
    assertiveRegion.setAttribute('aria-atomic', 'true');
    assertiveRegion.className = 'visually-hidden';
    assertiveRegion.textContent = ''; // Must be empty on load

    // Append to body
    document.body.appendChild(politeRegion);
    document.body.appendChild(assertiveRegion);
  }

  /**
   * Attach event listeners to quantity controls
   */
  function attachQuantityListeners() {
    // Listen for clicks on increase/decrease buttons
    // Use capture phase to intercept before theme's handlers
    document.addEventListener('click', function(event) {
      var target = event.target.closest('[data-action="decrease-quantity"], [data-action="increase-quantity"]');
      if (target) {
        handleQuantityButtonClick(event, target);
      }
    }, true); // Use capture phase

    // Listen for manual input changes
    document.addEventListener('change', function(event) {
      if (event.target.matches('.quantity-selector__value')) {
        handleQuantityInputChange(event.target);
      }
    });
  }

  /**
   * Handle quantity button clicks (+ / -)
   */
  function handleQuantityButtonClick(button) {
    var action = button.getAttribute('data-action');
    var newQuantity = parseInt(button.getAttribute('data-quantity'));
    var lineIndex = button.getAttribute('data-line');

    // For page reload mode (cart page), changes will be announced on next page load
    // For AJAX mode (mini-cart), store state and announce after update
    if (window.theme && window.theme.cartType !== 'page') {
      storeLineItemState(lineIndex, newQuantity, action);
    }
  }

  /**
   * Handle manual quantity input changes
   */
  function handleQuantityInputChange(input) {
    var newQuantity = parseInt(input.value) || 0;
    var lineIndex = input.getAttribute('data-line');

    // For page reload mode (cart page), changes will be announced on next page load
    // For AJAX mode (mini-cart), store state and announce after update
    if (window.theme && window.theme.cartType !== 'page') {
      storeLineItemState(lineIndex, newQuantity, 'input-change');
    }
  }

  /**
   * Announce cart contents on page load
   * This runs after page reload to inform screen reader users of cart state
   */
  function announceCartContentsOnLoad() {
    // Wait for page to fully render
    setTimeout(function() {
      var cartItems = document.querySelectorAll('.line-item');

      if (cartItems.length === 0) {
        announceStatus('Your cart is empty');
        return;
      }

      // Build announcement for each item
      var announcements = [];
      cartItems.forEach(function(item) {
        var titleElement = item.querySelector('.line-item__title');
        var quantityElement = item.querySelector('.quantity-selector__value');

        if (titleElement && quantityElement) {
          var productName = titleElement.textContent.trim();
          var quantity = quantityElement.value;
          announcements.push(productName + ', quantity ' + quantity);
        }
      });

      // Announce cart contents
      if (announcements.length > 0) {
        var itemCount = announcements.length;
        var itemText = itemCount === 1 ? 'item' : 'items';
        var fullAnnouncement = 'Cart has ' + itemCount + ' ' + itemText + ': ' + announcements.join('; ');
        announceStatus(fullAnnouncement);
      }
    }, 800); // Wait 800ms for cart to render
  }

  /**
   * Store line item state before update
   */
  function storeLineItemState(lineIndex, newQuantity, action) {
    // Get current cart state before change
    var currentQuantity = getCurrentLineQuantity(lineIndex);

    // Store in session storage temporarily
    sessionStorage.setItem('cart-aria-pending-update', JSON.stringify({
      lineIndex: lineIndex,
      oldQuantity: currentQuantity,
      newQuantity: newQuantity,
      action: action,
      timestamp: Date.now()
    }));

    // Set a timeout to check for updates (after cart re-renders)
    setTimeout(checkForCartUpdate, 500);
  }

  /**
   * Get current quantity for a line item
   */
  function getCurrentLineQuantity(lineIndex) {
    var input = document.querySelector('.quantity-selector__value[data-line="' + lineIndex + '"]');
    return input ? parseInt(input.getAttribute('data-current-value') || input.value) : 0;
  }

  /**
   * Check if cart was updated and announce changes
   */
  function checkForCartUpdate() {
    var pendingUpdate = sessionStorage.getItem('cart-aria-pending-update');
    if (!pendingUpdate) {
      return;
    }

    try {
      var updateData = JSON.parse(pendingUpdate);

      // Check if update is stale (more than 5 seconds old)
      if (Date.now() - updateData.timestamp > 5000) {
        sessionStorage.removeItem('cart-aria-pending-update');
        return;
      }

      // Get updated quantity
      var currentQuantity = getCurrentLineQuantity(updateData.lineIndex);

      // Get product name for the announcement
      var productName = getProductName(updateData.lineIndex);

      // Announce the change
      if (currentQuantity === 0) {
        announceStatus(productName + ' removed from cart');
      } else if (updateData.oldQuantity !== currentQuantity) {
        var quantityChange = currentQuantity - updateData.oldQuantity;
        var direction = quantityChange > 0 ? 'increased' : 'decreased';
        announceStatus(productName + ' quantity ' + direction + ' to ' + currentQuantity);
      }

      // Get and announce cart total
      announceCartTotal();

      // Clear pending update
      sessionStorage.removeItem('cart-aria-pending-update');
    } catch (e) {
      console.error('Cart ARIA announcement error:', e);
      sessionStorage.removeItem('cart-aria-pending-update');
    }
  }

  /**
   * Get product name for a line item
   */
  function getProductName(lineIndex) {
    // Try to find product name in cart table or mini-cart
    var lineItem = document.querySelector('[data-line="' + lineIndex + '"]');
    if (!lineItem) {
      return 'Item';
    }

    // Navigate up to find the product title
    var productRow = lineItem.closest('.line-item, .mini-cart__item');
    if (!productRow) {
      return 'Item';
    }

    var titleElement = productRow.querySelector('.line-item__title, .mini-cart__item-title');
    return titleElement ? titleElement.textContent.trim() : 'Item';
  }

  /**
   * Announce cart total
   */
  function announceCartTotal() {
    // Get cart total from the page
    var totalElement = document.querySelector('.cart-recap__price-line-price, .mini-cart__total-price');
    if (totalElement) {
      var totalText = totalElement.textContent.trim();
      // Add a slight delay to allow quantity announcement to complete
      setTimeout(function() {
        announceStatus('Cart total: ' + totalText);
      }, 500);
    }
  }

  /**
   * Announce a status message to screen readers
   */
  function announceStatus(message) {
    var liveRegion = document.getElementById('cart-status-announcements');
    if (!liveRegion) {
      return;
    }

    // Clear previous message
    liveRegion.textContent = '';

    // Use setTimeout to ensure screen readers detect the change
    setTimeout(function() {
      liveRegion.textContent = message;

      // Clear after 5 seconds to prevent stale announcements
      setTimeout(function() {
        liveRegion.textContent = '';
      }, 5000);
    }, 100);
  }

  /**
   * Announce an error message to screen readers
   */
  function announceError(message) {
    var liveRegion = document.getElementById('cart-error-announcements');
    if (!liveRegion) {
      return;
    }

    // Clear previous message
    liveRegion.textContent = '';

    // Use setTimeout to ensure screen readers detect the change
    setTimeout(function() {
      liveRegion.textContent = message;

      // Clear after 5 seconds
      setTimeout(function() {
        liveRegion.textContent = '';
      }, 5000);
    }, 100);
  }

  /**
   * Handle cart refresh events (triggered by theme)
   */
  function handleCartRefresh() {
    // Reattach listeners after cart re-render
    setTimeout(function() {
      announceStatus('Cart updated');
    }, 300);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after Shopify section re-renders (for cart page)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('[data-section-type="cart"]')) {
      init();
    }
  });

})();
